class VideoSoundToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-video-sound-toggle]');
    if (!this.button) return;

    this.handleClick = this.handleClick.bind(this);
    this.button.addEventListener('click', this.handleClick);

    // `video-background` injects its <template> lazily, and a desktop/mobile player
    // only hydrates once a breakpoint reveals it. Every player arrives muted, so
    // re-apply the button's state whenever a new one shows up.
    const container = this.closest('.video-background');
    if (!container) return;

    this.videoObserver = new MutationObserver(() =>
      this.setMuted(this.button.classList.contains('is-muted'))
    );
    this.videoObserver.observe(container, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.handleClick);
    if (this.videoObserver) this.videoObserver.disconnect();
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.setMuted(!this.button.classList.contains('is-muted'));
  }

  setMuted(muted) {
    this.button.classList.toggle('is-muted', muted);
    this.button.setAttribute('aria-pressed', String(muted));
    this.button.setAttribute(
      'aria-label',
      muted ? this.button.dataset.unmuteLabel : this.button.dataset.muteLabel
    );

    const container = this.closest('.video-background');
    if (!container) return;

    // Only the visible player stays audible. A container can hold both a desktop and
    // a mobile video, and the one a media query hides keeps playing after a viewport
    // change — zero client rects means an ancestor is `display: none`.
    container
      .querySelectorAll('video')
      .forEach((video) => this.applyMuted(video, muted || video.getClientRects().length === 0));
  }

  applyMuted(video, muted) {
    video.muted = muted;
    video.defaultMuted = muted;

    if (muted) {
      video.setAttribute('muted', '');
      return;
    }

    video.removeAttribute('muted');
    video.volume = 1;

    // Some browsers won't resume audible playback from just toggling
    // `.muted` on an already-autoplaying video — force a replay while
    // we're still inside the click handler's user-gesture context.
    const playResult = video.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {});
    }
  }
}

customElements.get('video-sound-toggle') || customElements.define('video-sound-toggle', VideoSoundToggle);
